// Production line items editor — styles. (Materials live in shared MaterialsEditor.)
import React from "react";
import { Trash2, Image as ImageIcon, Plus, Camera } from "lucide-react";
import { compressImageFile } from "../lib/image";
import { MaterialsEditor, materialsSubtotal, newMaterial } from "./MaterialsEditor";

export { newMaterial };

export function newProductionStyle() {
  return {
    id: Math.random().toString(36).slice(2, 10),
    image: null, product_name: "", style_no: "",
    num_pieces: "1", pattern_base: "", pattern_grading: "",
    grading_sizes: "",
    pricing_type: "fob", production_price: "",
  };
}

export function computeProductionTotals(items, materials, gstRate = 5) {
  let styles = 0;
  items.forEach((it) => {
    const pieces = Number(it.num_pieces || 0);
    const prod = Number(it.production_price || 0) * pieces;
    const patternBase = Number(it.pattern_base || 0);
    const gradingPerSize = Number(it.pattern_grading || 0);
    const gradingSizes = Number(it.grading_sizes || 0);
    const grading = gradingPerSize * gradingSizes;
    styles += patternBase + grading + prod;
  });
  const mats = materialsSubtotal(materials);
  const subtotal = styles + mats;
  const gst = (subtotal * gstRate) / 100;
  return { styles, mats, subtotal, gst, total: subtotal + gst };
}

async function fileToDataUrl(file) {
  return compressImageFile(file);
}

export function ProductionItemsEditor({ items, materials, onItemsChange, onMaterialsChange, gstRate = 5 }) {
  const u = (id, patch) => onItemsChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => onItemsChange(items.filter((it) => it.id !== id));

  const pickImg = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-pick of same file
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { alert("Image must be under 15 MB"); return; }
    u(id, { image: await fileToDataUrl(file) });
  };

  const t = computeProductionTotals(items, materials, gstRate);

  return (
    <div>
      <div className="sg-section-h">Styles</div>
      {items.map((it, idx) => {
        const pieces = Number(it.num_pieces || 0);
        const lineTotal = Number(it.pattern_base || 0) + Number(it.pattern_grading || 0) + Number(it.production_price || 0) * pieces;
        return (
          <div key={it.id} style={cardStyle} data-testid={`prod-style-${idx}`}>
            <div style={rowHead}>
              <span style={itemNo}>Style #{idx + 1}</span>
              {items.length > 1 && <button onClick={() => removeItem(it.id)} style={removeBtn}><Trash2 size={14} color="var(--danger)" /></button>}
            </div>
            <div style={imageSlot}>
              {it.image ? (
                <img src={it.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={imgPh}>
                  <ImageIcon size={22} color="var(--muted)" />
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>Add a product photo</span>
                </div>
              )}
            </div>
            <div style={imgBtnRow}>
              <label style={imgBtn} data-testid={`prod-camera-${idx}`}>
                <Camera size={14} color="var(--brand)" />
                <span style={imgBtnText}>Camera</span>
                <input type="file" accept="image/*" capture="environment" onChange={(e) => pickImg(it.id, e)} style={{ display: "none" }} />
              </label>
              <label style={imgBtn} data-testid={`prod-gallery-${idx}`}>
                <ImageIcon size={14} color="var(--brand)" />
                <span style={imgBtnText}>Gallery</span>
                <input type="file" accept="image/*" onChange={(e) => pickImg(it.id, e)} style={{ display: "none" }} />
              </label>
            </div>
            <label className="sg-label">Product name</label>
            <input className="sg-input" value={it.product_name || ""} onChange={(e) => u(it.id, { product_name: e.target.value })} placeholder="e.g. Linen co-ord set" />
            <div style={row2}>
              <div style={col}>
                <label className="sg-label">Style No.</label>
                <input className="sg-input" value={it.style_no || ""} onChange={(e) => u(it.id, { style_no: e.target.value })} placeholder="ST-001" />
              </div>
              <div style={col}>
                <label className="sg-label">No. of pieces</label>
                <input className="sg-input" value={it.num_pieces || ""} onChange={(e) => u(it.id, { num_pieces: e.target.value })} placeholder="100" inputMode="numeric" />
              </div>
            </div>
            <div style={row2}>
              <div style={col}>
                <label className="sg-label">Pattern (Base) ₹</label>
                <input className="sg-input" value={it.pattern_base || ""} onChange={(e) => u(it.id, { pattern_base: e.target.value })} inputMode="numeric" />
              </div>
              <div style={col}>
                <label className="sg-label">Grading / size ₹</label>
                <input className="sg-input" value={it.pattern_grading || ""} onChange={(e) => u(it.id, { pattern_grading: e.target.value })} inputMode="numeric" />
              </div>
            </div>
            <div style={row2}>
              <div style={col}>
                <label className="sg-label">No. of sizes graded</label>
                <input className="sg-input" value={it.grading_sizes || ""} onChange={(e) => u(it.id, { grading_sizes: e.target.value })} inputMode="numeric" placeholder="0" />
              </div>
              <div style={col} />
            </div>

            <label className="sg-label">Pricing model</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["fob", "jobwork"].map((pt) => {
                const active = (it.pricing_type || "fob") === pt;
                return (
                  <button key={pt} type="button" onClick={() => u(it.id, { pricing_type: pt })}
                    style={{
                      flex: 1, padding: 10, borderRadius: 10,
                      border: `1px solid ${active ? "var(--brand)" : "var(--line)"}`,
                      background: active ? "var(--brand)" : "var(--surface)",
                      color: active ? "#fff" : "var(--ink)", fontWeight: 700, fontSize: 12, cursor: "pointer",
                    }}
                    data-testid={`prod-pricing-${idx}-${pt}`}>
                    {pt === "fob" ? "FOB" : "Jobwork"}
                  </button>
                );
              })}
            </div>

            <label className="sg-label">Production price (₹ / pc) — {(it.pricing_type || "fob") === "fob" ? "FOB" : "Jobwork"}</label>
            <input className="sg-input" value={it.production_price || ""} onChange={(e) => u(it.id, { production_price: e.target.value })} inputMode="numeric" placeholder="0" />

            <div style={lineTotalRow}>
              <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase" }}>Total for this style</span>
              <span style={{ fontSize: 14, color: "var(--ink)", fontWeight: 700 }}>{fmt(lineTotal)}</span>
            </div>
          </div>
        );
      })}
      <button type="button" onClick={() => onItemsChange([...items, newProductionStyle()])} style={addBtn}>
        <Plus size={16} color="var(--brand)" /><span style={{ color: "var(--brand)", fontWeight: 700 }}>Add another style</span>
      </button>

      <MaterialsEditor materials={materials} onChange={onMaterialsChange} sectionTitle="Materials" />

      <div style={summary}>
        <SRow l="Styles subtotal" v={fmt(t.styles)} />
        <SRow l="Materials subtotal" v={fmt(t.mats)} />
        <SRow l="Subtotal" v={fmt(t.subtotal)} />
        <SRow l={`GST @ ${gstRate}%`} v={fmt(t.gst)} />
        <div style={{ borderTop: "1px solid var(--line)", marginTop: 8, paddingTop: 10 }}>
          <SRow l="Total" v={fmt(t.total)} big />
        </div>
      </div>
    </div>
  );
}

function SRow({ l, v, big }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span style={{ fontSize: big ? 14 : 13, color: "var(--muted)", fontWeight: big ? 800 : 600 }}>{l}</span>
      <span style={{ fontSize: big ? 20 : 14, color: "var(--ink)", fontWeight: big ? 800 : 600 }}>{v}</span>
    </div>
  );
}

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const cardStyle = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, padding: 14, marginBottom: 12 };
const rowHead = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
const itemNo = { fontSize: 11, fontWeight: 800, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 0.6 };
const removeBtn = { width: 30, height: 30, borderRadius: 8, background: "var(--surface-subtle)", border: 0, cursor: "pointer", display: "grid", placeItems: "center" };
const imageSlot = { display: "block", height: 140, background: "var(--surface-subtle)", borderRadius: 10, overflow: "hidden", border: "1px dashed var(--line)", marginBottom: 8 };
const imgPh = { width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 };
const imgBtnRow = { display: "flex", gap: 8, marginBottom: 12 };
const imgBtn = { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface)", cursor: "pointer" };
const imgBtnText = { fontSize: 12, fontWeight: 700, color: "var(--brand)" };
const row2 = { display: "flex", gap: 8 };
const col = { flex: 1 };
const addBtn = { display: "flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%", padding: 12, border: "1px dashed var(--line)", borderRadius: 10, background: "var(--surface)", cursor: "pointer", marginBottom: 14 };
const summary = { background: "var(--surface-subtle)", borderRadius: 12, padding: 14, marginBottom: 8 };
const lineTotalRow = { display: "flex", justifyContent: "space-between", paddingTop: 8, marginTop: 4, borderTop: "1px solid var(--line)" };
